import * as _ from 'lodash';
import * as React from 'react';
import * as Validator from 'validator';
import * as Model from '../../api/model';
import * as Request from '../../api/request';
import { WithStyles, Theme, TableRow, TableCell, Tooltip, IconButton, CircularProgress, Snackbar, FormControl, Input, FormHelperText, Select, MenuItem } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import withStyles, { CSSProperties } from '@material-ui/core/styles/withStyles';
import { API } from '../../api';

//#region UI configuration
const strings = {
    deleteButton: 'Delete',
    saveButton: 'Save',
    cancelButton: 'Cancel',
    deleteError: 'Unable to delete (item can be used)',
    editError: 'Unable to edit, try again',
    namePlaceholder: 'Enter name',
    nameIsRequired: 'Name is required',
    parentIsRequired: 'Region is required'
};

const styles = (theme: Theme) => ({
    formControl: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center'
    } as CSSProperties,
    formField: {
        width: 300,
        marginRight: 8
    } as CSSProperties
});

type Styles = WithStyles<'formControl' | 'formField'>;
//#endregion

//#region Form configuration
const enum FormFieldNames {
    NAME = 'name',
    REGION = 'region'
}

interface FormFields {
    name: string;
    region: { id: number, value: string } | null;
}

interface FormErrors {
    name?: string;
    region?: string;
}
//#endregion

//#region Props & State
interface SubregionEditItemProps {
    item: Model.SubregionInfo;
    selected: boolean;
    onItemSelected: (id: number) => void
    onItemEdited: (model: Model.SubregionInfo) => void
    onItemRemoved: (id: number) => void
}
interface SubregionEditItemState {
    removing: boolean;
    saving: boolean;
    loading: boolean;
    error: null | string;
    fields: FormFields;
    errors: FormErrors;
    parents: Model.Region[];
    showParents: boolean;
}

type PropsWithStyles = SubregionEditItemProps & Styles;

const initialState = (item: Model.SubregionInfo): SubregionEditItemState => ({
    removing: false,
    saving: false,
    loading: false,
    error: null,
    fields: {
        name: item.name,
        region: { id: item.region.id, value: item.region.name }
    },
    errors: {},
    parents: [],
    showParents: false
});
//#endregion

class SubregionEditItem extends React.Component<PropsWithStyles, SubregionEditItemState> {

    private nameInput: HTMLInputElement;

    constructor(props: PropsWithStyles) {
        super(props);

        this.state = initialState(props.item);
    }

    render() {
        const { classes, item, selected, onItemSelected } = this.props;
        const { removing, saving, error, errors, fields, parents, loading, showParents } = this.state;

        return (
            <React.Fragment>
                {!selected && <TableRow hover={true} selected={selected} onClick={() => onItemSelected(item.id)}>
                    <TableCell numeric={true}>{item.id}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.region.name}</TableCell>
                    <TableCell>
                        {!removing && <Tooltip title={strings.deleteButton}>
                            <IconButton onClick={this.onDeleteClick}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>}
                        {removing && <CircularProgress size={45} />}
                    </TableCell>
                </TableRow>}

                {selected && <TableRow selected={true}>
                    <TableCell numeric={true}>
                        {!saving && <React.Fragment>
                            <Tooltip title={strings.cancelButton}>
                                <IconButton onClick={() => onItemSelected(item.id)}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={strings.saveButton}>
                                <IconButton onClick={this.onSaveClick}>
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                        </React.Fragment>}
                        {saving && <CircularProgress size={45} />}
                    </TableCell>
                    <TableCell>
                        <FormControl margin="none" className={classes.formControl} error={!!errors.name}>
                            <Input
                                className={classes.formField}
                                id={FormFieldNames.NAME}
                                inputRef={ref => this.nameInput = ref}
                                name={FormFieldNames.NAME}
                                placeholder={strings.namePlaceholder}
                                onChange={this.onFieldChange}
                                type="text"
                                value={fields.name}
                            />
                            <FormHelperText>{errors.name}</FormHelperText>
                        </FormControl>
                    </TableCell>
                    <TableCell>
                        <FormControl margin="none" className={classes.formControl} error={!!errors.region}>
                            <Select
                                className={classes.formField}
                                value={fields.region ? fields.region.id : 0}
                                onChange={this.onParentSelectChange}
                                displayEmpty={true}
                                onOpen={this.onParentSelectOpen}
                                onClose={this.onParentSelectClose}
                                open={showParents}
                                renderValue={value => (<span>{fields.region ? fields.region.value : ''}</span>)}
                                inputProps={{
                                    name: FormFieldNames.REGION,
                                    id: FormFieldNames.REGION,
                                }}
                            >
                                {parents.map(parent => (
                                    <MenuItem key={parent.id} value={parent.id}>{parent.name}</MenuItem>
                                ))}
                            </Select>
                            {loading && <CircularProgress size={24} />}
                            <FormHelperText>{errors.region}</FormHelperText>
                        </FormControl>
                    </TableCell>
                    <TableCell>
                        {!removing && <Tooltip title={strings.deleteButton}>
                            <IconButton onClick={this.onDeleteClick}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>}
                        {removing && <CircularProgress size={45} />}
                    </TableCell>
                </TableRow>}

                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={this.onErrorClose}
                    message={<span>{error}</span>}
                    action={[
                        <IconButton
                            key="close"
                            color="inherit"
                            onClick={this.onErrorClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    ]}
                />
            </React.Fragment>
        );
    }

    private onDeleteClick = () => {
        const { item, onItemRemoved } = this.props;
        this.setState({ removing: true });
        API.subregion.delete(item.id)
            .then(() => {
                onItemRemoved(item.id);
            })
            .catch(() => {
                this.setState({ removing: false, error: strings.deleteError });
            });
    }

    private onErrorClose = () => {
        this.setState({ error: null });
    }

    private onFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            fields: { ...this.state.fields, [event.target.name]: event.target.value }
        });
    }

    private onSaveClick = () => {
        const errors: FormErrors = this.validateFormFields(this.state.fields);
        this.setState({ errors });

        if (_.isEmpty(errors) && this.state.fields.region) {
            const { item, onItemEdited } = this.props;
            const { name, region } = this.state.fields;

            this.setState({ saving: true });

            API.subregion.edit(item.id, new Request.Subregion(region.id, name))
                .then(model => {
                    onItemEdited(model);
                    this.setState({ saving: false });
                })
                .catch(() => {
                    this.setState({ saving: false, error: strings.editError });
                });
        } else if (!!errors.name) {
            this.nameInput.focus();
        }
    }

    private validateFormFields(fields: FormFields): FormErrors {
        const errors: FormErrors = {};

        if (Validator.isEmpty(fields.name)) {
            errors.name = strings.nameIsRequired;
        }

        if (!fields.region || fields.region.id <= 0) {
            errors.region = strings.parentIsRequired;
        }

        return errors;
    }

    private onParentSelectOpen = () => {
        const { parents, loading } = this.state;
        if (parents.length === 0 && !loading) {
            this.setState({ loading: true });
            API.region.list().then(items => {
                this.setState({ loading: false, parents: items, showParents: true });
            });
        } else {
            this.setState({ showParents: true });
        }
    }

    private onParentSelectClose = () => {
        this.setState({ showParents: false });
    }

    private onParentSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { parents } = this.state;
        const selectedId = parseInt(event.target.value, 10);
        const selected = _.find(parents, (o) => o.id === selectedId);
        if (selected) {
            this.setState({
                fields: { ...this.state.fields, [event.target.name]: { id: selected.id, value: selected.name } }
            });
        }
    }
}

export default withStyles(styles)<SubregionEditItemProps>(SubregionEditItem);



// WEBPACK FOOTER //
// ./src/modules/subregion/SubregionEditItem.tsx